// =============================================================================
// Optim TURPE V2 — CE QU'IL FAUT DEMANDER AU FOURNISSEUR (DEV-2166, M1 de la
// revue du 25/09/2026).
//
// Ce que le client doit vivre : sous le chiffre de tête, la liste des valeurs à
// reporter dans sa demande (formule cible, puissance de chaque classe avec son
// unité, date du barème, identifiant de l'étude), et un bouton pour la copier.
// C'est une liste de valeurs, pas un courrier : certains fournisseurs ont leur
// propre formulaire, et la démarche reste celle du client.
//
// Source : `restitution.demande` et `restitution.marche_a_suivre`, servis par
// le moteur (courtage#251, `modules/optimturpe/demande.py`) : l'écran lit la
// formule et les puissances servies, copie les `lignes[]` servies telles
// quelles et affiche les étapes servies. Sur une étude antérieure au lot, la
// liste est composée depuis la structure RETENUE par le moteur, sans aucun
// chiffre calculé ici.
// =============================================================================

import { lireChiffre } from "@modules/courtage/utils/optim-turpe-v2.chiffre"
import {
    formatDateFr,
    formatPuissanceFr,
    getUnitePuissance,
    PLAGE_ORDER,
} from "@modules/courtage/utils/optim-turpe-v2.helpers"
import {
    LIBELLE_CLASSE_HORAIRE,
    libelleFormuleTarifaire,
} from "@modules/courtage/utils/optim-turpe-v2.libelles"
import type { OptimTurpeV2Etude } from "@modules/courtage/types/optim-turpe-v2"

export type LigneDemande = {
    libelle: string
    valeur: string
    /** Ce qui est souscrit aujourd'hui, dit à côté de la valeur (demande servie). */
    precision?: string
    /**
     * La colonne « Aujourd'hui » du dossier (lot D) : la valeur en place, lue
     * dans la demande servie ou dans la structure actuelle ; `null` quand elle
     * n'est pas connue (jamais un 0).
     */
    aujourdhui?: string | null
    /** Vrai quand la valeur demandée est celle déjà en place. */
    inchangee?: boolean
    /** Vrai pour le barème et la référence : une note du dossier, pas une ligne du tableau. */
    reference?: boolean
    /**
     * Le texte de la ligne quand il ne s'écrit pas « libellé : valeur » (la
     * référence courte de l'étude, « Réf. 00C4A1 »).
     */
    texte?: string
}

/**
 * La référence d'une étude telle qu'un client la voit (DEV-2311, 05/10/2026) :
 * les six derniers caractères de l'identifiant, en majuscules, « Réf. 00C4A1 ».
 * Même format que le rapport PDF, pour que le support retrouve l'étude ;
 * l'identifiant complet reste dans le seul dossier de l'équipe.
 */
export function referenceCourteEtude(identifiant: string): string {
    return `Réf. ${identifiant.trim().slice(-6).toUpperCase()}`
}

/** Le texte d'une ligne de la demande, à l'écran comme dans la copie. */
export function texteDeLigne(ligne: LigneDemande): string {
    return ligne.texte ?? `${ligne.libelle} : ${ligne.valeur}`
}

/** La ligne « Référence de l'étude » d'une étude, en référence courte. */
function ligneReference(identifiant: string): LigneDemande {
    const texte = referenceCourteEtude(identifiant)
    return {
        libelle: "Réf.",
        valeur: texte.slice("Réf. ".length),
        reference: true,
        texte,
    }
}

const PREFIXE_REFERENCE_SERVIE = /^Référence de l'étude\s*:\s*(\S+)\s*$/
const PREFIXE_FORMULE_SERVIE = "Formule tarifaire d'acheminement :"

/**
 * Une ligne copiée telle que le moteur la sert, sauf deux : la référence de
 * l'étude, qui s'écrit en référence courte, et la formule, qui s'écrit avec son
 * libellé devant son code (la ligne composée par la page, mêmes valeurs).
 */
function ligneCopieeDuMoteur(
    ligne: string,
    lignesServies: LigneDemande[]
): string {
    const reference = PREFIXE_REFERENCE_SERVIE.exec(ligne.trim())
    if (reference) return referenceCourteEtude(reference[1])
    if (ligne.startsWith(PREFIXE_FORMULE_SERVIE)) {
        const formule = lignesServies.find(
            (l) => l.libelle === "Formule tarifaire d'acheminement"
        )
        if (formule)
            return formule.precision
                ? `${texteDeLigne(formule)} (${formule.precision})`
                : texteDeLigne(formule)
    }
    return ligne
}

export type DemandeLue =
    | {
          etat: "aucune_demarche"
          /** La phrase servie par le moteur, quand il la sert. */
          libelle: string | null
      }
    | {
          etat: "demande"
          lignes: LigneDemande[]
          /** Le texte copié, une valeur par ligne (servi tel quel quand il l'est). */
          copie: string[]
          /** Les étapes d'avant l'envoi (« Avant d'envoyer »). */
          etapes: string[]
          /**
           * Ce qui se fait après l'envoi (« Après l'envoi ») : la vérification
           * sur la facture suivante, tant que le journal n'est pas servi.
           */
          apresEnvoi: string[]
          /** Vrai quand la liste vient du moteur, faux quand l'écran l'a composée. */
          servie: boolean
          /**
           * `recommandee` : la structure est à demander (un gain, ou le risque
           * C5 sous lequel le moteur sert la demande, D9). `saisie` : la
           * structure saisie par le client, chiffrée telle quelle ; ce n'est
           * pas la recommandation (D17). Contrat v1.2 §10 : la liste « malgré
           * tout » d'un surcoût est retirée.
           */
          portee: "recommandee" | "saisie"
      }

/**
 * États où la liste s'affiche : un gain (contrat v1.2 §7 ; le surcoût n'a
 * plus de liste « malgré tout », §10). Jamais sur un montant non servi, un
 * refus ou un échec ; « aucune démarche » sur une structure déjà optimale.
 */
const ETATS_A_DEMANDER = ["economie", "gain_par_depassements_evites"]

/** Code servi de l'étape unique d'une structure sans démarche. */
const ETAPE_AUCUNE = "aucune-demarche"

/** Code servi de l'étape qui se fait après l'envoi. */
const ETAPE_APRES_ENVOI = "verifier-la-facture"

type EtapeLue = { code: string | null; libelle: string }

function etapesServies(etude: OptimTurpeV2Etude): EtapeLue[] {
    const marche = etude.marche_a_suivre
    if (!Array.isArray(marche)) return []
    return marche
        .filter(
            (etape) =>
                typeof etape?.libelle === "string" &&
                etape.libelle.trim().length > 0
        )
        .map((etape) => ({
            code: typeof etape.code === "string" ? etape.code : null,
            libelle: String(etape.libelle),
        }))
}

/** La phrase « aucune démarche » servie, ou `null`. */
function aucuneDemarcheServie(etapes: EtapeLue[]): string | null {
    return etapes.length === 1 && etapes[0].code === ETAPE_AUCUNE
        ? etapes[0].libelle
        : null
}

/**
 * La marche à suivre SERVIE par le moteur, celle du rapport PDF (A13 : l'écran
 * aligné sur le PDF, quatre étapes dont la vérification du raccordement quand
 * elle s'impose) ; vide quand le moteur ne la sert pas ou n'a rien à faire
 * faire : l'écran compose alors son repli.
 */
export function etapesDeLaDemande(etude: OptimTurpeV2Etude): string[] {
    return etapesServies(etude)
        .filter((e) => e.code !== ETAPE_AUCUNE)
        .map((e) => e.libelle)
}

function nombre(valeur: unknown): number | null {
    return typeof valeur === "number" && Number.isFinite(valeur) ? valeur : null
}

/**
 * Les valeurs de la demande SERVIE : formule, puissance de chaque classe avec
 * son unité, barème et référence de l'étude. Mêmes libellés que les lignes
 * copiées, qui restent celles du moteur.
 */
function lignesDeLaDemandeServie(
    demande: NonNullable<OptimTurpeV2Etude["demande"]>
): LigneDemande[] {
    const lignes: LigneDemande[] = []
    const fta = demande.formule_tarifaire_acheminement
    if (fta?.cible) {
        // DEV-2311 : le libellé, puis le CODE entre parenthèses (le
        // fournisseur en a besoin) ; un code inconnu s'écrit seul.
        const actuelle = fta.actuelle
            ? libelleFormuleTarifaire(fta.actuelle)
            : null
        const cible = libelleFormuleTarifaire(fta.cible)
        lignes.push({
            libelle: "Formule tarifaire d'acheminement",
            valeur: cible,
            precision: fta.change
                ? `aujourd'hui ${actuelle || "non connue"}`
                : "inchangée",
            aujourdhui: actuelle || (fta.change ? null : cible),
            inchangee: !fta.change,
        })
    }
    for (const p of demande.puissances ?? []) {
        const cible = nombre(p?.cible)
        if (cible === null) continue
        const unite = p.unite ?? demande.unite_puissance ?? null
        const actuelle = nombre(p.actuelle)
        lignes.push({
            libelle:
                p.classe && p.classe !== "unique"
                    ? `Puissance souscrite ${p.classe}`
                    : "Puissance souscrite",
            valeur: formatPuissanceFr(cible, unite),
            precision: !p.change
                ? "inchangée"
                : actuelle === null
                  ? undefined
                  : `aujourd'hui ${formatPuissanceFr(actuelle, unite)}`,
            aujourdhui:
                actuelle !== null
                    ? formatPuissanceFr(actuelle, unite)
                    : p.change
                      ? null
                      : formatPuissanceFr(cible, unite),
            inchangee: !p.change,
        })
    }
    const bareme = formatDateFr(demande.date_bareme)
    if (bareme) {
        lignes.push({
            libelle: "Barème d'acheminement de référence",
            valeur: bareme,
            reference: true,
        })
    }
    if (demande.identifiant_etude) {
        lignes.push(ligneReference(demande.identifiant_etude))
    }
    return lignes
}

function copieDe(lignes: LigneDemande[]): string[] {
    return lignes.map(texteDeLigne)
}

/**
 * La demande de l'étude, ou `null` quand il n'y a rien à dire : scénario saisi
 * (ce n'est pas notre recommandation), surcoût, état inconnu, structure absente.
 */
export function lireDemande(etude: OptimTurpeV2Etude): DemandeLue | null {
    // Un refus ou un échec n'a pas de structure à demander.
    if (etude.status === "FAILURE" || etude.error?.code) return null
    const chiffre = lireChiffre(etude)
    const saisie = chiffre.estScenarioLibre === true
    const etapes = etapesServies(etude)
    const aucune = aucuneDemarcheServie(etapes)
    const servie = etude.demande
    const lignesServies = servie ? lignesDeLaDemandeServie(servie) : []
    const avantEnvoi = (liste: EtapeLue[]) =>
        liste.filter((e) => e.code !== ETAPE_APRES_ENVOI).map((e) => e.libelle)
    const apresEnvoi = (liste: EtapeLue[]) =>
        liste.filter((e) => e.code === ETAPE_APRES_ENVOI).map((e) => e.libelle)
    const demandeServie = (portee: "recommandee" | "saisie"): DemandeLue => {
        const copieServie = (servie?.lignes ?? [])
            .filter(
                (l): l is string => typeof l === "string" && l.trim() !== ""
            )
            .map((l) => ligneCopieeDuMoteur(l, lignesServies))
        return {
            etat: "demande",
            lignes: lignesServies,
            copie:
                copieServie.length > 0 ? copieServie : copieDe(lignesServies),
            etapes: avantEnvoi(etapes),
            apresEnvoi: apresEnvoi(etapes),
            servie: true,
            portee,
        }
    }
    if (
        !saisie &&
        (chiffre.etat === "deja_optimale" || chiffre.etat === "surcout")
    ) {
        // D9 (lot 4a) : sous le risque C5, le moteur sert la demande vers la
        // puissance adaptée, même sur un état sans économie ; D17 : la jambe
        // retenue contre le conseil porte sa demande. Le moteur ne sert
        // aucune demande sur un « Rien à changer » ordinaire (`demande.py`) :
        // une demande servie sur ces états est donc à lire.
        if (servie && lignesServies.length > 0)
            return demandeServie("recommandee")
        return { etat: "aucune_demarche", libelle: aucune }
    }
    if (!saisie && (!chiffre.etat || !ETATS_A_DEMANDER.includes(chiffre.etat)))
        return null
    if (saisie && chiffre.montantServi === false) return null
    const portee = saisie ? ("saisie" as const) : ("recommandee" as const)

    // 1. Ce que le moteur sert passe avant toute composition de l'écran. Sur
    //    un gain, une demande servie nulle avec la seule étape « aucune
    //    démarche » dit que rien ne change.
    if (portee === "recommandee" && servie === null && aucune !== null) {
        return { etat: "aucune_demarche", libelle: aucune }
    }
    if (servie && lignesServies.length > 0) return demandeServie(portee)
    const etapesDuRepli = aucune === null ? etapes : []

    // 2. Repli : composée depuis la structure retenue par le moteur.

    const structure = etude.meilleure?.structure_tarifaire
    const fta = structure?.formule_tarifaire_acheminement?.fta
    const puissance = structure?.puissance_souscrite_soutirage
    // La structure en place, pour la colonne « Aujourd'hui » : lue, jamais déduite.
    const enPlace = etude.actuelle?.structure_tarifaire
    const ftaEnPlace = enPlace?.formule_tarifaire_acheminement?.fta || null
    const puissanceEnPlace = enPlace?.puissance_souscrite_soutirage
    const unite = getUnitePuissance(etude)
    const lignes: LigneDemande[] = []
    if (fta) {
        lignes.push({
            libelle: "Formule tarifaire d'acheminement",
            valeur: libelleFormuleTarifaire(fta),
            aujourdhui: ftaEnPlace ? libelleFormuleTarifaire(ftaEnPlace) : null,
            inchangee: ftaEnPlace !== null && ftaEnPlace === fta,
        })
    }
    if (typeof puissance?.puissance === "number") {
        const avant = nombre(puissanceEnPlace?.puissance)
        lignes.push({
            libelle: "Puissance souscrite",
            valeur: formatPuissanceFr(puissance.puissance, unite),
            aujourdhui: avant !== null ? formatPuissanceFr(avant, unite) : null,
            inchangee: avant !== null && avant === puissance.puissance,
        })
    } else {
        for (const classe of PLAGE_ORDER) {
            const valeur = puissance?.denivele?.[classe]
            if (typeof valeur !== "number") continue
            const avant = nombre(puissanceEnPlace?.denivele?.[classe])
            lignes.push({
                libelle: `Puissance souscrite ${classe}`,
                valeur: formatPuissanceFr(valeur, unite),
                aujourdhui:
                    avant !== null ? formatPuissanceFr(avant, unite) : null,
                inchangee: avant !== null && avant === valeur,
            })
        }
    }
    // Sans formule ni puissance, il n'y a rien à demander : on ne fabrique pas
    // une demande vide.
    if (lignes.length === 0) return null
    const bareme = formatDateFr(etude.calcul?.bareme?.date_retenue)
    if (bareme) {
        lignes.push({
            libelle: "Date du barème TURPE",
            valeur: bareme,
            reference: true,
        })
    }
    if (etude._id) {
        lignes.push(ligneReference(etude._id))
    }
    return {
        etat: "demande",
        lignes,
        copie: copieDe(lignes),
        etapes: avantEnvoi(etapesDuRepli),
        apresEnvoi: apresEnvoi(etapesDuRepli),
        servie: false,
        portee,
    }
}

/**
 * Ce qui identifie le contrat et l'étude dans le courriel (contrat v1.2 §7.3).
 * Une valeur inconnue reste entre crochets dans l'aperçu, que l'utilisateur
 * complète avant de copier ; rien n'est inventé.
 */
export type ContexteDuDossier = {
    /** Le PDL complet (complet dans la lettre, R-T16). */
    pdl?: string | null
    /** Le site (nom du bâtiment), en fin d'objet ; absent, l'objet s'arrête au PDL. */
    site?: string | null
    segment?: string | null
    /** Lot 4g (api-metier#1607) : le titulaire du contrat et l'adresse du point. */
    titulaire?: string | null
    adresse?: string | null
    /** La signature, préremplie depuis la session (C28). */
    signataire?: string | null
    societe?: string | null
    /** « 05/10/2026 » : la date de calcul de l'étude. */
    date?: string | null
    /** L'identifiant de l'étude ; le courriel en écrit la référence courte. */
    identifiant?: string | null
    /** C5, D7 : le palier de 3 kVA supérieur servi, ou `null`. */
    repliKva?: number | null
    /** D17 : les valeurs sont celles du client, pas la recommandation. */
    pasLaRecommandation?: boolean
}

/**
 * « Demande de modification de … » : ce qui change réellement, formule
 * d'abord. Une ligne inchangée ne s'y nomme pas ; une ligne dont l'état n'est
 * pas connu (étude ancienne sans structure en place) s'y nomme.
 */
export function titreDeLaDemande(demande: { lignes: LigneDemande[] }): string {
    const valeurs = demande.lignes.filter((l) => !l.reference && !l.inchangee)
    const fta = valeurs.some(
        (l) => l.libelle === "Formule tarifaire d'acheminement"
    )
    const puissances = valeurs.filter((l) =>
        l.libelle.startsWith("Puissance souscrite")
    )
    const unique =
        puissances.length > 0 &&
        puissances.every((l) => l.libelle === "Puissance souscrite")
    const objets = [
        fta ? "de la formule tarifaire d'acheminement" : null,
        puissances.length === 0
            ? null
            : unique
              ? "de la puissance souscrite"
              : "des puissances souscrites",
    ].filter(Boolean)
    return objets.length > 0
        ? `Demande de modification ${objets.join(" et ")}`
        : "Demande de modification"
}

/**
 * Fait 16, « ce que l'étude ne prend pas en compte » : les exclusions du
 * rapport. Source : courtage `modules/optimturpe/rapport/mentions.py`,
 * `EXCLUSIONS`. La restitution ne les sert pas encore (lot moteur prévu par la
 * cible v0.3) : en attendant, l'écran les recopie, et un changement côté
 * courtage doit être reporté ici.
 *
 * Vague wording du 14/10/2026 : « option tarifaire » y est devenu « formule
 * tarifaire d'acheminement » (Q1, lexique de la partie 4) et le plafonnement
 * des dépassements obtenu sur demande est retiré (Q38), comme dans le PDF
 * servi depuis courtage v183 (`mentions.py`, `limites_de_l_etude`). Les
 * phrases y diffèrent encore de quelques mots (« votre gestionnaire de
 * réseau », « en basse tension jusqu'à 36 kVA, la formule tarifaire
 * d'acheminement » à l'écran ; « le gestionnaire de réseau », « la formule »
 * au PDF).
 */
export const EXCLUSIONS_DE_L_ETUDE: readonly string[] = [
    "Le prix des prestations facturées par votre gestionnaire de réseau pour modifier le contrat.",
    "Les conditions d'une nouvelle modification du contrat : en basse tension jusqu'à 36 kVA, la formule tarifaire d'acheminement ne peut changer qu'une fois par période de douze mois, et une hausse de puissance souscrite demandée moins de douze mois après une baisse est facturée.",
    // Q38 (14/10/2026) : « le plafonnement des dépassements obtenu sur
    // demande » est retiré, aucun texte TURPE ne le fonde.
    "Un changement de contrat survenu pendant la période analysée.",
    "La part fourniture et les taxes, qui ne relèvent pas de l'acheminement.",
    "L'évolution future de votre consommation et des barèmes.",
]

/**
 * Les « Limites de l'étude » d'un compteur (Q11, P7, I14) : seulement les
 * règles de son segment. La règle des douze mois ne vaut qu'en basse tension
 * jusqu'à 36 kVA (C5) ; elle ne s'écrit pas sur un C4 ou un C2.
 */
export function exclusionsDuSegment(
    segment: string | null | undefined
): readonly string[] {
    return segment && segment !== "C5"
        ? EXCLUSIONS_DE_L_ETUDE.filter((e) => !e.includes("douze mois"))
        : EXCLUSIONS_DE_L_ETUDE
}

/** Une condition qui pèse sur l'envoi, et le champ où agir (contrat §7.2). */
export type ConditionAvantDEnvoyer = {
    texte: string
    /** Vrai quand « Corriger » mène au champ de la puissance de raccordement. */
    raccordement: boolean
}

function nombreServi(valeur: unknown): number | null {
    return typeof valeur === "number" && Number.isFinite(valeur) ? valeur : null
}

/**
 * « Avant d'envoyer » (contrat v1.2 §7.2, C29) : seulement les conditions qui
 * pèsent sur cette demande. Une puissance demandée au-dessus du raccordement
 * connu ; en C5, la réserve datée de changement récent servie ; les valeurs
 * prises par défaut qui pèsent, nommées. Vide sinon (le bloc ne s'affiche pas).
 */
export function conditionsAvantDEnvoyer(
    etude: OptimTurpeV2Etude,
    valeursParDefaut: string[] = []
): ConditionAvantDEnvoyer[] {
    const conditions: ConditionAvantDEnvoyer[] = []
    const borne = etude.bornes?.raccordement
    const valeur = nombreServi(borne?.valeur)
    const cibles = (etude.demande?.puissances ?? [])
        .map((p) => nombreServi(p?.cible))
        .filter((v): v is number => v !== null)
    const forme = borne?.alerte?.forme
    const auDessus =
        forme === "ecartee-avec-demarche" ||
        forme === "levee-au-dessus" ||
        (valeur !== null &&
            cibles.length > 0 &&
            Math.max(...cibles) > valeur &&
            (borne?.statut === "ecartee" || borne?.statut === "levee"))
    if (auDessus)
        conditions.push({
            texte: "Une puissance supérieure à la puissance de raccordement demande une étude du gestionnaire de réseau : travaux et délai possibles.",
            raccordement: true,
        })
    if (etude.compteur?.segment === "C5") {
        const recente = (etude.reserves ?? []).find(
            (r) => r?.code === "modification-contractuelle-recente"
        )
        const texte = recente?.label?.trim()
        if (texte) conditions.push({ texte, raccordement: false })
    }
    if (valeursParDefaut.length > 0)
        conditions.push({
            texte:
                valeursParDefaut.length > 1
                    ? `Vérifier les valeurs prises par défaut : ${valeursParDefaut.join(", ")}.`
                    : `Vérifier la valeur prise par défaut : ${valeursParDefaut[0]}.`,
            raccordement: false,
        })
    return conditions
}

// =============================================================================
// Le COURRIEL de la demande (contrat v1.2 §7.3, maquette 2c ; D3 a, D5, D7 a,
// D17 a ; C28).
//
// Ce que le courtier doit vivre : un seul courriel, neutre, à la première
// personne, quel que soit celui qui l'envoie (aucune voix, aucun mandat, D5).
// Un objet, l'appel, une ouverture unique, l'identité du point, seulement ce
// qui change (avant → après, terme officiel et code complet), l'origine des
// valeurs, la demande de confirmation, la clôture et la signature. Aucun
// montant, aucun scénario, aucun mot de l'étude, jamais « nous ».
// =============================================================================

/** « Puissance souscrite en heures de pointe (PTE) » pour « Puissance souscrite PTE ». */
export function libelleEnClair(libelle: string): string {
    const m = /^Puissance souscrite (\S+)$/.exec(libelle)
    if (!m) return libelle
    const clair = LIBELLE_CLASSE_HORAIRE[m[1]]
    return clair
        ? `Puissance souscrite en ${clair} (${m[1]})`
        : `Puissance souscrite ${m[1]}`
}

/** Le segment du point, en clair (contrat §7.3). */
const SEGMENT_EN_CLAIR: Record<string, string> = {
    C5: "C5, basse tension jusqu'à 36 kVA",
    C4: "C4, basse tension au-delà de 36 kVA",
    C2: "C2, haute tension",
}

/** « Formule tarifaire d'acheminement : longue … (HTALU5) → courte … (HTACU5) ». */
function ligneDuCourriel(ligne: LigneDemande, repliKva: number | null): string {
    const libelle = libelleEnClair(ligne.libelle)
    const sansMot = (v: string) => v.replace(/^formule /, "")
    const valeur = sansMot(ligne.valeur)
    const repli =
        repliKva !== null && ligne.libelle === "Puissance souscrite"
            ? ` Si ${ligne.valeur.replace(/\u00a0/g, " ")} n'est pas proposé, ${repliKva} kVA.`
            : ""
    if (ligne.aujourdhui)
        return `– ${libelle} : ${sansMot(ligne.aujourdhui)} → ${valeur}${repli ? `.${repli}` : ""}`
    return `– ${libelle} : ${valeur} (valeur actuelle non connue)${repli ? `.${repli}` : ""}`
}

/**
 * Le courriel de la demande, tel que l'aperçu l'affiche avant la copie. Les
 * valeurs inconnues restent entre crochets ; les espaces insécables de l'écran
 * deviennent des espaces simples.
 */
export function courrielDeLaDemande(
    demande: Extract<DemandeLue, { etat: "demande" }>,
    contexte: ContexteDuDossier
): string {
    const nonVide = (v: string | null | undefined): v is string =>
        typeof v === "string" && v.trim() !== ""
    const pdl = nonVide(contexte.pdl) ? contexte.pdl.trim() : "[numéro complet]"
    const changees = demande.lignes.filter((l) => !l.reference && !l.inchangee)
    const segment = nonVide(contexte.segment)
        ? (SEGMENT_EN_CLAIR[contexte.segment] ?? contexte.segment)
        : null
    const reference = nonVide(contexte.identifiant)
        ? referenceCourteEtude(contexte.identifiant).replace(/^Réf\. /, "réf. ")
        : null
    const origine = [
        "Ces valeurs viennent d'une étude",
        nonVide(contexte.date) ? ` du ${contexte.date}` : "",
        reference ? ` (${reference})` : "",
        ", calculée sur le barème d'acheminement en vigueur à cette date. Le rapport est joint.",
    ].join("")
    const signature = `${nonVide(contexte.signataire) ? contexte.signataire.trim() : "[Prénom Nom]"}, ${nonVide(contexte.societe) ? contexte.societe.trim() : "[Société]"}`
    const blocs: string[][] = [
        [
            `Objet : Modification du contrat d'acheminement · PDL ${pdl}${nonVide(contexte.site) ? ` · ${contexte.site.trim()}` : ""}`,
        ],
        ["Madame, Monsieur,"],
        [
            "Je vous demande de modifier le contrat d'acheminement du point de livraison ci-dessous et de transmettre cette demande au gestionnaire de réseau.",
        ],
        [
            `Point de livraison (PDL) : ${pdl}`,
            `Site : ${nonVide(contexte.titulaire) ? contexte.titulaire.trim() : "[titulaire]"}, ${nonVide(contexte.adresse) ? contexte.adresse.trim() : "[adresse]"}`,
            ...(segment ? [`Segment : ${segment}`] : []),
        ],
        [
            "Modifications demandées :",
            ...changees.map((l) =>
                ligneDuCourriel(l, contexte.repliKva ?? null)
            ),
        ],
        [
            ...(contexte.pasLaRecommandation
                ? [
                      "Ces valeurs sont celles que j'ai choisies ; ce ne sont pas celles recommandées par l'étude.",
                  ]
                : []),
            origine,
        ],
        [
            "Merci de me confirmer la prise en compte de cette demande, sa date d'effet et le coût éventuel de la prestation du gestionnaire de réseau.",
        ],
        [
            "Je vous prie d'agréer, Madame, Monsieur, l'expression de mes salutations distinguées.",
        ],
        [signature],
    ]
    return blocs
        .filter((b) => b.length > 0)
        .map((b) => b.join("\n"))
        .join("\n\n")
        .replace(/[\u00a0\u202f]/g, " ")
}

/**
 * « Ensuite » (contrat v1.2 §7.4, A4) : après l'envoi, trois lignes, avant le
 * rapport ; les mêmes que le PDF.
 */
export const ENSUITE: readonly string[] = [
    "Le fournisseur transmet la demande au gestionnaire de réseau, qui fait le changement. Cette prestation peut être facturée.",
    "Vérifiez sur la facture suivante que les nouvelles valeurs sont appliquées.",
    "Refaites l'étude dans un an, ou si l'activité du site change.",
]

/**
 * Montant non servi (maquette, état « mns ») : la marche à suivre servie pour
 * rendre l'étude chiffrable, ou une liste vide. Jamais composée par l'écran.
 */
export function marcheNonChiffree(etude: OptimTurpeV2Etude): string[] {
    if (etude.status === "FAILURE" || etude.error?.code) return []
    const chiffre = lireChiffre(etude)
    if (chiffre.estScenarioLibre) return []
    if (chiffre.montantServi !== false && chiffre.etat !== "montant_non_servi")
        return []
    return etapesServies(etude)
        .filter((e) => e.code !== ETAPE_AUCUNE)
        .map((e) => e.libelle)
}
